import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

// URL state mínimo de /clients/archivo. Schema PROPIO (no extiende el
// del listado principal) — los campos del listado operativo (tab,
// vigencia, completitud, drawer abierto, etc.) no aplican acá.
//
// Lo que SÍ aplica:
//   - search: buscador por nombre/RUT (reusa tsquery del listClients)
//   - desde / hasta: rango de fecha de archivado (clients.deletedAt)
//   - ejecutivoId: ejecutivo histórico al momento del archivado
//   - page: paginación

export const clientsArchiveSearchSchema = z.object({
  search: z.string().optional(),
  desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional(),
  hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional(),
  ejecutivoId: z.union([z.string().uuid(), z.literal('sin')]).optional(),
  page: z.coerce.number().int().min(1).default(1),
});

export type ClientsArchiveSearchParams = z.infer<typeof clientsArchiveSearchSchema>;

export function useClientsArchiveTableState() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/clients/archivo' }) as ClientsArchiveSearchParams;

  const update = useCallback(
    (patch: Partial<ClientsArchiveSearchParams>) => {
      const next = { ...search, ...patch };
      // Cualquier cambio de filtro resetea la paginación.
      const filterChanged =
        'search' in patch ||
        'desde' in patch ||
        'hasta' in patch ||
        'ejecutivoId' in patch;
      if (filterChanged && !('page' in patch)) next.page = 1;
      void navigate({
        to: '/clients/archivo',
        search: next as never,
      });
    },
    [navigate, search],
  );

  const reset = useCallback(() => {
    void navigate({ to: '/clients/archivo', search: { page: 1 } as never });
  }, [navigate]);

  return { search, update, reset };
}
